import { type DocNode } from '@atlaskit/adf-schema';
import { reduce } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { ROVO_SKILL_COMBINED_EXTENSION_KEY } from '@atlassian/conversation-assistant-content-transformer/utils/skills';

import { isFirstPartyLink } from './isFirstPartyLink';
import type { ContentFeatures } from './types';
import { wordCount } from './wordCount';

const countThirdPartySmartLink = (
	currentValue: ContentFeatures,
	url: string | undefined,
): ContentFeatures => {
	if (!url || isFirstPartyLink(url)) {
		return currentValue;
	}
	const count3PSources = currentValue.count3PSources + 1;
	return {
		...currentValue,
		count3PSources,
		has3PSources: true,
	};
};

const processADFNode = (currentValue: ContentFeatures, node: ADFEntity): ContentFeatures => {
	switch (node.type) {
		case 'text': {
			const linkMark = node.marks?.find((mark) => mark.type === 'link');
			const linkCount = linkMark ? currentValue.linkCount + 1 : currentValue.linkCount;
			const linkHref = typeof linkMark?.attrs?.href === 'string' ? linkMark.attrs.href : undefined;
			return countThirdPartySmartLink(
				{
					...currentValue,
					textLength: currentValue.textLength + (node.text ? node.text.length : 0),
					wordCount: currentValue.wordCount + wordCount(node.text),
					linkCount,
				},
				linkHref,
			);
		}
		case 'mention':
			return {
				...currentValue,
				mentionCount: currentValue.mentionCount + 1,
			};
		case 'inlineCard':
		case 'embedCard':
		case 'blockCard':
			return countThirdPartySmartLink(
				{
					...currentValue,
					linkCount: currentValue.linkCount + 1,
				},
				typeof node.attrs?.url === 'string' ? node.attrs.url : undefined,
			);
		case 'inlineExtension':
			if (
				node.attrs?.extensionKey === ROVO_SKILL_COMBINED_EXTENSION_KEY &&
				node.attrs?.parameters?.slug
			) {
				return {
					...currentValue,
					explicitSkills: [...currentValue.explicitSkills, node.attrs.parameters.slug],
				};
			}
			return currentValue;
	}
	return currentValue;
};

export const extractContentFeatures = (adf: DocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		adfLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		linkCount: 0,
		mentionCount: 0,
		explicitSkills: [],
		// Initialised because the reducer only assigns these on a 3P hit — pages with zero/only-1P sources must still emit stable neutral values to Data Portal.
		has3PSources: false,
		count3PSources: 0,
	};

	return reduce<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};
